/**
 * What happened to a Y.Text since the editor last saw it — EXACTLY, from Yjs's
 * own change events, never by diffing two texts (#987 review).
 *
 * The editor lags its Y.Text by up to a frame (useCollabText snapshots in a
 * rAF), so a keystroke can be based on text a collaborator has since changed.
 * writeEditorChange then rebases the keystroke over those changes. A diff of
 * the two texts guesses what changed — it put a keystroke between two remote
 * edits on one line at the line's edge, swallowed a delete there, and cost
 * seconds on a large file a collaborator had just reformatted. The change
 * events say what changed, at what it cost Yjs to do it.
 *
 * The log keeps the last LOG_SIZE transactions, each with the text it left,
 * and at most LOG_MAX_CHARS of text in all (a large file keeps fewer). The
 * editor catches up right after each edit it reports (CodeMirrorModifier), so
 * it is behind by a frame's worth of remote changes at most.
 *
 * Accepted: when the text the editor saw occurs twice in the log (type "a",
 * delete it), the newer one is taken; the changes between were then a no-op
 * on the text, and the edit lands on valid text either way.
 */
import { ChangeSet } from '@codemirror/state'
import type * as Y from 'yjs'

const LOG_SIZE = 16
const LOG_MAX_CHARS = 4_000_000

interface Entry { changes: ChangeSet; after: string }
interface Log { entries: Entry[]; users: number; observer: (e: Y.YTextEvent) => void }

const logs = new WeakMap<Y.Text, Log>()

/** One Yjs text event as a CodeMirror ChangeSet over the text before it. */
function changeSetOf(event: Y.YTextEvent, afterLength: number): ChangeSet {
  const specs: Array<{ from: number; to?: number; insert?: string }> = []
  let pos = 0
  let inserted = 0
  let deleted = 0
  for (const op of event.delta) {
    if (op.retain) pos += op.retain
    else if (op.delete) { specs.push({ from: pos, to: pos + op.delete }); pos += op.delete; deleted += op.delete }
    else if (typeof op.insert === 'string') {
      // CodeMirror text counts "\r\n" as one character, Yjs as two: such a change can't be expressed exactly.
      if (op.insert.includes('\r')) throw new Error('carriage return in a text change')
      specs.push({ from: pos, insert: op.insert })
      inserted += op.insert.length
    }
  }
  return ChangeSet.of(specs, afterLength - inserted + deleted)
}

/** Start logging `ytext`'s changes (the hosts call it while their editor is open). Returns the stop function. */
export function trackTextChanges(ytext: Y.Text): () => void {
  let log = logs.get(ytext)
  if (!log) {
    const entries: Entry[] = []
    const observer = (event: Y.YTextEvent) => {
      const after = ytext.toString()
      try {
        entries.push({ changes: changeSetOf(event, after.length), after })
      } catch {
        entries.length = 0 // an event we can't read: forget the history rather than trust it
        return
      }
      let chars = 0
      for (const e of entries) chars += e.after.length
      while (entries.length > 1 && (entries.length > LOG_SIZE || chars > LOG_MAX_CHARS)) chars -= entries.shift()!.after.length
    }
    log = { entries, users: 0, observer }
    ytext.observe(observer)
    logs.set(ytext, log)
  }
  log.users++
  const mine = log
  return () => {
    if (--mine.users > 0) return
    ytext.unobserve(mine.observer)
    logs.delete(ytext)
  }
}

/**
 * The changes that took the Y.Text from `base` to its current text, or null when
 * the log can't say (not tracked, `base` too old, or not a text it held).
 */
export function changesSince(ytext: Y.Text, base: string): ChangeSet | null {
  const entries = logs.get(ytext)?.entries
  // Only a log that ends at the text the Y.Text holds now can say how it got there (an outer transaction's
  // earlier changes aren't logged until it ends).
  if (!entries?.length || entries[entries.length - 1]!.after !== ytext.toString()) return null
  for (let i = entries.length - 1; i >= 0; i--) {
    if (entries[i]!.after !== base) continue
    let out = ChangeSet.empty(base.length)
    for (let j = i + 1; j < entries.length; j++) {
      const next = entries[j]!.changes
      if (next.length !== out.newLength) return null
      out = out.compose(next)
    }
    return out
  }
  return null
}
